import { FC, useMemo, useState, MouseEvent } from 'react';

import { KeyMetricsResponseDto } from 'src/generated/services/TFinancialApi';
import IconReceipt from 'src/assets/images/icons/analytic-receipt.svg';
import IconDollar from 'src/assets/images/icons/dollar-square.svg';
import IconReceipt2 from 'src/assets/images/icons/receipt-orange.svg';
import IconWarning from 'src/assets/images/icons/analytic-warning-error.svg';
import IconUser from 'src/assets/images/icons/user-tick.svg';
import IconBox from 'src/assets/images/icons/box-remove.svg';
import IconQuestion from 'src/assets/images/icons/keymetric-icon.svg';
import { cn, getDeviationAmountColor } from 'src/utils/utility';
import { CardTheme, ICardAnalyticItemProps, MetricConfig } from '../../molecules/card/cardAnalytic/types';
import { DashboardMetricLoader } from '../../organisms/dashboard/MetricLoader';

import { CardMetricEdit } from '../../molecules/card/cardAnalytic/CardMetricEdit';
import { InternalDashboardCard } from '../../molecules/card/InternalDashboardCard';
import { MetricsSelector } from './MetricsSelector';
import { MetricsSelectorPopover } from '../../molecules/MetricsSelector';
import { CardId } from './types';

// Custom card wrapper for Dashboard page with specific title styling
const DashboardMetricCard: FC<{ config: MetricConfig; filterCount: number; isLoading: boolean }> = ({ config, filterCount, isLoading }) => {
  const { title, icon, totalCount, totalAmount, currency = 'SGD', theme, isIncludeAmout, customHeader, customBody, isNetAmount } = config;

  const currencyColor = useMemo(() => {
    switch (theme) {
      case CardTheme.ORANGE:
        return 'text-[#F79824]';
      case CardTheme.PURPLE:
        return 'text-[#CB6FF6]';
      case CardTheme.GREEN:
        return 'text-[#16AD89]';
      case CardTheme.TEAL:
        return 'text-[#16AD89]';
      case CardTheme.BLUE_LIGHT:
        return 'text-[#4476F6]';
      case CardTheme.NEUTRAL:
        return 'text-[#CB6FF6]';
      case CardTheme.BLUE:
        return 'text-[#495C8C]';
      case CardTheme.RED:
        return 'text-[#e72547]';
      default:
        return 'text-neutral-100';
    }
  }, [theme]);

  const getGradientColor = (theme: CardTheme): string => {
    switch (theme) {
      case CardTheme.ORANGE:
        return 'rgba(247, 152, 36, 0.70)';
      case CardTheme.PURPLE:
        return 'rgba(203, 111, 246, 0.70)';
      case CardTheme.GREEN:
        return 'rgba(22, 173, 137, 0.70)';
      case CardTheme.TEAL:
        return 'rgba(22, 173, 137, 0.70)';
      case CardTheme.BLUE_LIGHT:
        return 'rgba(68, 118, 246, 0.70)';
      case CardTheme.NEUTRAL:
        return 'rgba(203, 111, 246, 0.70)';
      case CardTheme.BLUE:
        return 'rgba(73, 92, 140, 0.70)';
      case CardTheme.RED:
        return 'rgba(231, 37, 71, 0.70)';
      default:
        return 'rgba(203, 111, 246, 0.70)';
    }
  };

  const formatCount = (num: number) => num.toLocaleString();
  const formatNumber = (num: number) => num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Determine grid columns based on card configuration
  // Use minmax(0,1fr) to allow the first column to shrink, auto for data columns
  const gridCols = isIncludeAmout ? 'grid-cols-[minmax(0,1fr)_40px_1px_80px]' : 'grid-cols-[minmax(0,1fr)_40px]';
  const numCols = isIncludeAmout ? 4 : 2;

  return (
    <div
      className={cn('relative overflow-hidden border border-neutral-20 rounded-8 w-full px-12 py-10 bg-white', 'transition-all duration-300')}
      style={{
        boxShadow: '0 4px 7px 0 rgba(112, 149, 221, 0.12), 0 1px 0 0 #DFDFDF',
      }}
    >
      {/* Decorative gradient element */}
      <div
        className='absolute -right-[47.5px] -top-[85px] w-[120px] h-[98px] rounded-full'
        style={{
          backgroundColor: getGradientColor(theme),
          filter: 'blur(45px)',
        }}
      />

      {/* Custom layout for cards with customHeader/customBody */}
      {customHeader && customBody ? (
        <div className='flex flex-col'>
          {/* Header row */}
          <div className='flex items-center justify-between pb-6 mb-6 border-b border-neutral-20'>
            <div className='flex items-center gap-8'>
              <img src={icon} alt='' className='w-20 h-20 flex-shrink-0' />
              <span className='font-medium text-neutral-100 text-12 leading-[18px] h-[36px] flex items-center max-w-[70px]'>{title}</span>
            </div>
            {customHeader}
          </div>
          {/* Body */}

          {isLoading ? (
            <div className='flex flex-col gap-4'>
              <div className='h-16 bg-neutral-20 rounded w-full' />
              <div className='h-16 bg-neutral-20 rounded w-full' />
            </div>
          ) : (
            customBody
          )}
        </div>
      ) : (
        /* Default layout - separate header and body wrappers */
        <>
          {/* Header Row - wrapper with border */}
          <div className='border-b border-neutral-20 pb-6 mb-6'>
            <div className={cn('grid gap-x-10', gridCols)}>
              <div className='flex items-center gap-8 min-w-0 overflow-hidden'>
                <img src={icon} alt='' className='w-20 h-20 flex-shrink-0' />
                <span className='font-medium text-neutral-100 text-12 leading-[18px] h-[36px] flex items-center max-w-[70px]'>{title}</span>
              </div>
              {isIncludeAmout ? (
                <>
                  <div className='text-xs text-neutral-60 font-normal text-right flex items-center justify-end'>Count</div>
                  <div className='flex items-center justify-center'>
                    <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
                  </div>
                  <div className='text-xs text-neutral-60 font-normal text-left flex items-center gap-4'>
                    <span>{isNetAmount ? 'Net Amount' : 'Amount'}</span>
                    <span className={cn('font-bold', currencyColor)}>{currency}</span>
                  </div>
                </>
              ) : (
                <div className='text-right flex items-center justify-end'>
                  <span className={cn('font-bold text-xs', currencyColor)}>{currency}</span>
                </div>
              )}
            </div>
          </div>

          {/* Body Row */}
          <div>
            {isLoading ? (
              <div className='flex flex-col gap-4'>
                <div className='h-16 bg-neutral-20 rounded w-full' />
                <div className='h-16 bg-neutral-20 rounded w-full' />
              </div>
            ) : (
              <div className={cn('grid gap-x-10', gridCols)}>
                <span className='text-neutral-60 font-medium whitespace-nowrap text-xs'>Total:</span>
                {isIncludeAmout ? (
                  <>
                    <span className='font-bold text-neutral-100 text-right text-xs' title={formatCount(totalCount)}>
                      {formatCount(totalCount)}
                    </span>
                    <div className='flex items-center justify-center'>
                      <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
                    </div>
                    <span className={cn('font-bold text-left text-xs', getDeviationAmountColor(totalAmount))} title={formatNumber(totalAmount)}>
                      {formatNumber(totalAmount)}
                    </span>
                  </>
                ) : (
                  <span className='font-bold text-neutral-100 text-right text-xs' title={formatCount(totalCount)}>
                    {formatCount(totalCount)}
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface IDashboardMetricsProps {
  dataStats?: KeyMetricsResponseDto;
  items?: ICardAnalyticItemProps[];
  isLoading: boolean;
  id?: string;
  activeFilterCount?: number;
}

const STORAGE_KEY = 'dashboard_selected_metrics';

const getDefaultMetrics = (): CardId[] => [CardId.NRCF_SHIPMENTS, CardId.UNIT_PRICE_DEVIATIONS, CardId.QUANTITY_DEVIATIONS, CardId.UNKNOWN_CHARGES];

const loadMetricsFromStorage = (): CardId[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that it's an array and has valid CardIds
      if (Array.isArray(parsed) && parsed.length > 0 && parsed.length <= 4) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to load metrics from localStorage:', error);
  }
  return getDefaultMetrics();
};

const saveMetricsToStorage = (metrics: CardId[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(metrics));
  } catch (error) {
    console.error('Failed to save metrics to localStorage:', error);
  }
};

export const DashboardMetrics: FC<IDashboardMetricsProps> = ({ dataStats, items, isLoading, id, activeFilterCount = 0 }) => {
  const currency = dataStats?.meta?.currency || 'SGD';
  const metrics = dataStats?.keymetrics;

  // State for metric selection (only used when dataStats is provided, not items)
  // Initialize from localStorage or use defaults
  const [selectedMetrics, setSelectedMetrics] = useState<CardId[]>(() => loadMetricsFromStorage());

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getNumber = (val: number | undefined | null) => val || 0;

  // Map of all available metric configs by ID
  const allMetricConfigsMap: Partial<Record<CardId, MetricConfig>> = useMemo(() => {
    if (!metrics) return {} as Partial<Record<CardId, MetricConfig>>;

    return {
      [CardId.NRCF_SHIPMENTS]: {
        title: 'NRCF Shipments',
        icon: IconReceipt2,
        totalCount: getNumber(metrics.nrcfShipmentCount),
        totalAmount: getNumber(metrics.nrcfShipmentAmount?.afterTax),
        filteredCount: 0,
        filteredAmount: 0,
        unit: currency,
        theme: CardTheme.ORANGE,
        currency,
        isIncludeAmout: true,
      },
      [CardId.UNIT_PRICE_DEVIATIONS]: {
        title: 'Unit Price Deviations',
        icon: IconDollar,
        totalCount: getNumber(metrics.unitPriceDeviationCount),
        totalAmount: getNumber(metrics.unitPriceDeviationAmount?.afterTax),
        filteredCount: 0,
        filteredAmount: 0,
        unit: currency,
        theme: CardTheme.PURPLE,
        currency,
        isIncludeAmout: true,
        isNetAmount: true,
      },
      [CardId.QUANTITY_DEVIATIONS]: {
        title: 'Quantity Deviations',
        icon: IconBox,
        totalCount: getNumber(metrics.quantityDeviationCount),
        totalAmount: getNumber(metrics.quantityDeviationAmount?.afterTax),
        filteredCount: 0,
        filteredAmount: 0,
        unit: currency,
        theme: CardTheme.GREEN,
        currency,
        isIncludeAmout: true,
      },
      [CardId.UNKNOWN_CHARGES]: {
        title: 'Unknown Charges',
        icon: IconQuestion,
        totalCount: getNumber(metrics.unknownChargeDeviationCount),
        totalAmount: getNumber(metrics.unknownChargeDeviationAmount?.afterTax),
        filteredCount: 0,
        filteredAmount: 0,
        unit: currency,
        theme: CardTheme.GREEN,
        currency,
        isIncludeAmout: true,
      },
      [CardId.APPROVALS]: {
        title: 'Approvals',
        icon: IconUser,
        totalCount: 0,
        totalAmount: 0,
        filteredCount: 0,
        filteredAmount: 0,
        unit: 'm³',
        theme: CardTheme.GREEN,
        currency,
        isIncludeAmout: false,
        customHeader: (
          <div className='grid grid-cols-[50px_1px_80px] items-center gap-x-10 text-xs text-neutral-60 font-normal'>
            <div className='text-right'>Service</div>
            <div className='flex justify-center'>
              <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
            </div>
            <div className='text-left'>Accessorial</div>
          </div>
        ),
        customBody: (
          <div className='grid grid-cols-[1fr_50px_1px_80px] items-center gap-x-10 text-xs'>
            <span className='text-neutral-60 font-medium whitespace-nowrap'>Total:</span>
            <span className='font-bold text-neutral-100 text-right' title={`${getNumber(metrics.serviceTypeApprovalCount)}`}>
              {getNumber(metrics.serviceTypeApprovalCount)}
            </span>
            <div className='flex justify-center'>
              <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
            </div>
            <span className='font-bold text-neutral-100 text-left' title={`${getNumber(metrics.accessorialApprovalCount)}`}>
              {getNumber(metrics.accessorialApprovalCount)}
            </span>
          </div>
        ),
      },
      [CardId.TOTAL_DEVIATION]: {
        title: 'Total Deviation',
        icon: IconWarning,
        totalCount: getNumber(metrics.noDeviations),
        totalAmount: getNumber(metrics.totalDeviationAmount?.afterTax),
        filteredCount: 0,
        filteredAmount: 0,
        unit: '#',
        theme: CardTheme.RED,
        currency,
        isIncludeAmout: true,
        isNetAmount: true,
      },
      [CardId.TOTAL_FLAGGED]: {
        title: 'Total Flagged',
        icon: IconReceipt,
        totalCount: 0,
        totalAmount: 0,
        filteredCount: 0,
        filteredAmount: 0,
        unit: '#',
        theme: CardTheme.ORANGE,
        currency,
        isIncludeAmout: false,
        customHeader: (
          <div className='grid grid-cols-[50px_1px_60px_1px_50px] items-center gap-x-5 text-xs text-neutral-60 font-medium'>
            <div className='text-center'>Invoices</div>
            <div className='flex justify-center'>
              <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
            </div>
            <div className='text-center'>Shipments</div>
            <div className='flex justify-center'>
              <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
            </div>
            <div className='text-center'>Charges</div>
          </div>
        ),
        customBody: (
          <div className='grid grid-cols-[1fr_50px_1px_60px_1px_50px] items-center gap-x-5 text-xs'>
            <span className='text-neutral-60 font-medium whitespace-nowrap'>Total:</span>
            <span className='font-bold text-right text-neutral-100 mr-5' title={`${getNumber(metrics.noInvoices)}`}>
              {getNumber(metrics.noInvoices)}
            </span>
            <div className='flex justify-center'>
              <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
            </div>
            <span className='font-bold text-right text-neutral-100 mr-5' title={`${getNumber(metrics.noShipments)}`}>
              {getNumber(metrics.noShipments)}
            </span>
            <div className='flex justify-center'>
              <div className='h-[10px] w-[1px] bg-[#DFDFDF]' />
            </div>
            <span className='font-bold text-right text-neutral-100 mr-5' title={`${getNumber(metrics.noCharges)}`}>
              {getNumber(metrics.noCharges)}
            </span>
          </div>
        ),
      },
    };
  }, [metrics, currency]);

  // Get only the selected metric configs in the order they were selected
  const metricConfigs: MetricConfig[] = useMemo(() => {
    return selectedMetrics.map((id) => allMetricConfigsMap[id]).filter(Boolean);
  }, [selectedMetrics, allMetricConfigsMap]);

  return (
    <div id={id} className='pb-20 xl:pb-24 px-16 xl:px-24 border-b border-neutral-20'>
      {isLoading ? (
        <DashboardMetricLoader />
      ) : (
        <div className='flex items-center gap-8 xl:gap-16 w-full'>
          {items ? (
            // Internal Dashboard - show all items in 5 columns
            <div className='flex-1 grid grid-cols-5 gap-16'>
              {items.map((item) => (
                <InternalDashboardCard key={item.id} item={item} isLoading={isLoading} />
              ))}
            </div>
          ) : (
            // Main Dashboard - show selected metrics with edit button
            <>
              <div className='flex-1 flex items-center flex-wrap gap-8 xl:gap-16'>
                {metricConfigs.map((config, index) => {
                  const isTotalFlagged = config.title === 'Total Flagged';
                  // Total Flagged card needs more width due to 3 data columns
                  const flexClass = isTotalFlagged ? 'flex-[1.1]' : 'flex-1';
                  const minWidthClass = isTotalFlagged ? 'min-w-[280px]' : 'min-w-[200px]';
                  return (
                    <div key={`${config.title}-${index}`} className={cn(flexClass, minWidthClass, 'max-w-full')}>
                      <DashboardMetricCard config={config} filterCount={activeFilterCount} isLoading={isLoading} />
                    </div>
                  );
                })}
              </div>

              {/* Edit Button */}
              <div className='w-auto self-start'>
                <CardMetricEdit onClick={handleOpen} className='!h-[89px]' />
                <MetricsSelectorPopover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
                  <MetricsSelector
                    initialSelected={selectedMetrics}
                    onApply={(newSelected) => {
                      setSelectedMetrics(newSelected);
                      saveMetricsToStorage(newSelected);
                      handleClose();
                    }}
                    onClose={handleClose}
                  />
                </MetricsSelectorPopover>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
